import { CircleCircle } from '@calm/evexia-web-sdk';
import createRbcApi from '@rhg/libs/api/create-rbc-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import {
    invalidateQueryKey,
    queryKeys,
    useMutation,
} from '@rhg/libs/react-query';

type ErrorProps = {
    status: number;
};

export interface CreateRecCollectionParams {
    conversationId: string;
    circle: CircleCircle;
}

/**
 * Creates a new `RecCollection` (Care Plan) from an AI Router conversation via
 * `POST /v1/recommendations/recipients/{recipient_id}/collections`.
 *
 * The endpoint generates the recommendations (GRL rules + AI Router content for
 * the conversation) and groups them under a brand-new collection. In the
 * concierge GA flow it runs before the AI Router generate endpoint, not instead
 * of it.
 *
 * Unlike `GET /recommendations?forceRefresh=true`, this call is purely additive:
 * it never deletes existing recommendations or collections, which is what the
 * multi–Care Plan model requires. Callers should therefore *not* follow it with a
 * force refresh — the collection and recommendation caches are invalidated here
 * instead.
 */
export function useCreateRecCollection() {
    const { getAccessTokenSilently } = useAuth();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            conversationId,
            circle,
        }: CreateRecCollectionParams) => {
            const api = createRbcApi(getAccessTokenSilently);

            if (!circle.authorizationJWT) {
                throw new Error(
                    'useCreateRecCollection: no circle authorization'
                );
            }

            const response = await api.createV1RecCollection({
                circleAuthorizationToken: circle.authorizationJWT,
                recipientId: circle.recipient.id,
                rbcCreateRecCollectionRequest: {
                    aiConversationId: conversationId,
                },
            });

            return response.data;
        },
        onSuccess: (_data, { circle }) => {
            invalidateQueryKey([
                queryKeys.recCollections,
                circle.recipient.id,
            ]).catch((err: unknown) =>
                logger.error(
                    'useCreateRecCollection: failed to invalidate recCollections query',
                    {},
                    err as Error
                )
            );
            invalidateQueryKey([
                queryKeys.recommendations,
                circle.recipient.id,
                true,
            ]).catch((err: unknown) =>
                logger.error(
                    'useCreateRecCollection: failed to invalidate recommendations query',
                    {},
                    err as Error
                )
            );
            invalidateQueryKey([
                queryKeys.recommendations,
                circle.recipient.id,
                false,
            ]).catch((err: unknown) =>
                logger.error(
                    'useCreateRecCollection: failed to invalidate recommendations query',
                    {},
                    err as Error
                )
            );
        },
        retry: (failureCount, error: ErrorProps) =>
            error?.status === 502 && failureCount < 1,
    });

    return {
        createRecCollection: mutate,
        createRecCollectionAsync: mutateAsync,
        ...otherProps,
    };
}
